import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import MainStructure from "../components/containers/MainStructure";
import { webReports } from "../api/webReports.service";
import MetricBars from "../components/reports/MetricBars";
import ReportPedigree from "../components/reports/ReportPedigree";
import ShareButton from "../components/reports/ShareButton";
import {
  crossSuccessTier,
  formatScore,
  horseName,
  modelName,
  score,
} from "../components/reports/reportMetrics";
import "../assets/styles/pages/webReports.scss";
import useWebReportMeta from "../hooks/useWebReportMeta";
import ReportAccessGate, {
  useReportAccess,
} from "../components/reports/ReportAccessGate";

export default function WebReport() {
  const { token } = useParams();
  return (
    <ReportAccessGate key={token}>
      <WebReportContent />
    </ReportAccessGate>
  );
}

function WebReportContent() {
  const requireAuthentication = useReportAccess();
  const location = useLocation();
  const { token } = useParams();
  const ownedId = /^\d+$/.test(token) ? token : null;
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const collectionReturnPath = location.state?.returnTo;
  const canReturnToCollection =
    typeof collectionReturnPath === "string" &&
    /^\/reports\/collections\/(?:premium\/\d+|catalogue\/\d+|[^/?]+)(?:\?[^#]*)?$/.test(
      collectionReturnPath,
    );
  const reportReturnPath = canReturnToCollection
    ? collectionReturnPath
    : "/pedigree-analysis";
  const reportReturnLabel = canReturnToCollection
    ? location.state?.returnLabel ||
      (collectionReturnPath.includes("/premium/")
        ? "Multi report"
        : "Catalogue report")
    : "Pedigree reports";
  const sortedCrosses = [...(report?.crosses || [])].sort(
    (first, second) =>
      (Number(second.percentage) || 0) - (Number(first.percentage) || 0),
  );
  useWebReportMeta(
    report?.status === "completed" ? horseName(report) : "Pedigree report",
  );
  useEffect(() => {
    const controller = new AbortController();
    setReport(null);
    setError("");
    (ownedId
      ? webReports.ownedReport(ownedId, controller.signal)
      : webReports.report(token, controller.signal))
      .then(setReport)
      .catch((err) => {
        if (err.authenticationRequired) return requireAuthentication();
        if (err.name !== "AbortError") setError(err.message);
      });
    return () => controller.abort();
  }, [token, ownedId, reload, requireAuthentication]);
  return (
    <MainStructure>
      <main className="web-report">
        <Link className="report-back" to={reportReturnPath}>
          ← {reportReturnLabel}
        </Link>
        {error ? (
          <section className="report-panel report-state" role="alert">
            <h1>Report unavailable</h1>
            <p>{error}</p>
            <button onClick={() => setReload(reload + 1)}>Try again</button>
          </section>
        ) : !report ? (
          <div className="report-state" role="status">
            Loading pedigree analysis…
          </div>
        ) : report.status !== "completed" ? (
          <section className="report-panel report-state">
            <h1>
              {["error", "ignored"].includes(report.status)
                ? "This analysis needs attention"
                : "Your analysis is being prepared"}
            </h1>
            <p>
              {["error", "ignored"].includes(report.status)
                ? "Some pedigree information could not be analysed. Contact support for help."
                : "Check again in a moment to see the completed results."}
            </p>
            <button onClick={() => setReload(reload + 1)}>
              Refresh report
            </button>
          </section>
        ) : (
          <>
            <header className="report-hero">
              <div>
                <p className="report-eyebrow">
                  {modelName(report.ml_model)} pedigree intelligence · Report{" "}
                  {report.id}
                </p>
                <h1>{horseName(report)}</h1>
                <p>
                  {report.lot ? `Lot ${report.lot} · ` : ""}
                  {report.sire?.country || "—"} / {report.dam?.country || "—"}
                  {report.is_top_pick ? " · Top pick" : ""}
                </p>
              </div>
              <ShareButton path={report.web_url} />
            </header>
            <nav className="report-section-nav" aria-label="Report sections">
              <a href="#overview">Overview</a>
              <a href="#pedigree">Pedigree</a>
              <a href="#crosses">Crosses</a>
              <a href="#inbreeding">Inbreeding</a>
            </nav>
            <section className="report-overview" id="overview">
              <div className="report-score-card">
                <p className="report-eyebrow">Pedigree score</p>
                <strong>{formatScore(score(report))}</strong>
                <span>
                  {report.top_value != null
                    ? `Top ${report.top_value}%`
                    : "Population tier unavailable"}
                </span>
                <p>
                  {report.top_value != null
                    ? `Original model population tier. Smaller percentages indicate a more selective group.`
                    : "A population tier was not recorded for this report."}
                </p>
              </div>
              <div className="report-panel">
                <h2>Performance profile</h2>
                <MetricBars report={report} />
              </div>
            </section>
            <ReportPedigree pedigree={report.pedigree} />
            <div className="report-detail-grid">
              <section className="report-panel" id="crosses">
                <p className="report-eyebrow">Cross analysis</p>
                <h2>Cross success</h2>
                <p className="report-note">
                  Success percentages for the ancestral crosses in this
                  pedigree.
                </p>
                {sortedCrosses.length ? (
                  <div
                    className="report-table-scroll report-crosses-scroll"
                    tabIndex="0"
                    aria-label="Cross success results, scroll to see all rows"
                  >
                    <table>
                      <thead>
                        <tr>
                          <th>Sire line</th>
                          <th>Dam line</th>
                          <th>Success</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCrosses.map((cross, index) => (
                          <tr key={index}>
                            <td>{cross.sire}</td>
                            <td>{cross.dam}</td>
                            <td>
                              <strong className={`report-cross-value report-cross-value--${crossSuccessTier(cross.percentage)}`}>
                                {cross.percentage}%
                              </strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No cross data recorded.</p>
                )}
              </section>
              <section className="report-panel" id="inbreeding">
                <p className="report-eyebrow">Repeated bloodlines</p>
                <h2>Inbreeding</h2>
                {report.inbreeding?.length ? (
                  <ul
                    className="report-inbreeding"
                    tabIndex="0"
                    aria-label="Inbreeding results, scroll to see all ancestors"
                  >
                    {report.inbreeding.map((item, index) => (
                      <li key={index}>
                        <strong>{item.name}</strong>
                        <span>{item.value}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No repeated ancestors recorded.</p>
                )}
                <p className="report-note">
                  Select a repeated ancestor in the pedigree to highlight its
                  appearances.
                </p>
              </section>
            </div>
            {Number(report.ml_model) === 1 && (
              <section className="report-panel">
                <h2>Additional indicators</h2>
                <div className="report-indicators">
                  {[
                    ["Precocity", report.bool_pre],
                    ["Soundness", report.bool_sou],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>
                        {value == null
                          ? "Unavailable"
                          : value
                            ? "High potential"
                            : "Not flagged as high"}
                      </strong>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </MainStructure>
  );
}
